const roundCurrency = (value) =>
  Math.round((Number(value) || 0) * 100) / 100;

const clampPercentage = (value) => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized <= 0) return 0;
  return Math.min(100, normalized);
};

const toValidDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const isSameCalendarDay = (left, right) =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const formatDatePart = (date) =>
  new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);

const formatTimePart = (date) =>
  new Intl.DateTimeFormat("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);

const normalizePositiveNumber = (value, fallback = 0) => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized < 0) return fallback;
  return normalized;
};

const isFiniteNumber = (value) => Number.isFinite(Number(value));
const normalizeSessionId = (value) => String(value || "").trim();
const normalizeSessionIds = (sessionIds = []) => {
  const seen = new Set();
  const result = [];
  (Array.isArray(sessionIds) ? sessionIds : []).forEach((sessionId) => {
    const normalized = normalizeSessionId(sessionId);
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });
  return result;
};
const normalizePackageMinSessions = (rule) =>
  Math.max(0, Math.floor(Number(rule?.minSessions) || 0));
const sumAmounts = (values = []) =>
  roundCurrency(
    (Array.isArray(values) ? values : []).reduce(
      (sum, value) => sum + (Number(value) || 0),
      0,
    ),
  );

export const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);

export const resolveSessionId = (session = {}, fallback = "") =>
  String(session?.sessionId || session?._id || fallback);

export const formatSessionDateTimeRange = (session = {}) => {
  const start = toValidDate(session?.dateStart);
  const end = toValidDate(session?.dateEnd);

  if (!start && !end) return "";
  if (!start && end) {
    return `${formatDatePart(end)} ${formatTimePart(end)} Uhr`;
  }
  if (start && !end) {
    return `${formatDatePart(start)} ${formatTimePart(start)} Uhr`;
  }

  if (isSameCalendarDay(start, end)) {
    return `${formatDatePart(start)} ${formatTimePart(start)} - ${formatTimePart(end)} Uhr`;
  }

  return `${formatDatePart(start)} ${formatTimePart(start)} - ${formatDatePart(end)} ${formatTimePart(end)} Uhr`;
};

export const getSortedSessions = (sessions = []) =>
  [...(Array.isArray(sessions) ? sessions : [])].sort(
    (left, right) => (left?.sortOrder ?? 0) - (right?.sortOrder ?? 0)
  );

const getAllSessionIds = (sessions = []) =>
  normalizeSessionIds(
    getSortedSessions(sessions).map((session, index) =>
      resolveSessionId(session, `session-${index}`),
    ),
  );

export const getPricingRuleById = (pricingRules = [], ruleId = "") =>
  (Array.isArray(pricingRules) ? pricingRules : []).find(
    (rule) => String(rule?.ruleId || "") === String(ruleId || "")
  ) || null;

export const getPackageIncludedSessionIds = (rule, sessions = []) => {
  if (!rule || rule.type !== "package") return [];

  const allSessionIds = getSortedSessions(sessions)
    .map((session, index) => resolveSessionId(session, `session-${index}`))
    .filter(Boolean);

  // minSessions mode: user picks freely, no auto-selection
  const minSessions = Number(rule.minSessions) || 0;
  if (minSessions > 0) {
    return [];
  }

  const linkedSessionIds = (Array.isArray(rule.sessionIds) ? rule.sessionIds : [])
    .map((sessionId) => String(sessionId || "").trim())
    .filter(Boolean);

  if (linkedSessionIds.length === 0) {
    return allSessionIds;
  }

  const allSessionSet = new Set(allSessionIds);
  return normalizeSessionIds(
    linkedSessionIds.filter((sessionId) => allSessionSet.has(sessionId)),
  );
};

const getPerSessionPricingRules = (pricingRules = []) =>
  (Array.isArray(pricingRules) ? pricingRules : [])
    .map((rule, index) => ({
      rule,
      index,
      type: String(rule?.type || "").trim(),
      amount: roundCurrency(normalizePositiveNumber(rule?.amount, 0)),
      sessionIds: normalizeSessionIds(rule?.sessionIds),
      ruleId: String(rule?.ruleId || ""),
      label: String(rule?.label || "").trim(),
    }))
    .filter((entry) => entry.type === "per_session")
    .map((entry) => ({
      ...entry,
      sessionIdSet: new Set(entry.sessionIds),
    }));

const pickBestPerSessionRule = (sessionId, perSessionRules = []) => {
  const normalizedSessionId = normalizeSessionId(sessionId);
  if (!normalizedSessionId) return null;
  const candidates = (Array.isArray(perSessionRules) ? perSessionRules : []).filter(
    (entry) => entry.sessionIdSet.has(normalizedSessionId),
  );
  const fallbackCandidates =
    candidates.length > 0
      ? candidates
      : (Array.isArray(perSessionRules) ? perSessionRules : []).filter(
          (entry) => entry.sessionIds.length === 0,
        );

  if (fallbackCandidates.length === 0) return null;

  return fallbackCandidates.reduce((best, current) => {
    if (!best) return current;
    if (current.amount < best.amount) return current;
    if (current.amount === best.amount && current.index < best.index) return current;
    return best;
  }, null);
};

const resolvePerSessionPricingForSessions = ({
  sessionIds = [],
  pricingRules = [],
}) => {
  const normalizedSessionIds = normalizeSessionIds(sessionIds);
  const perSessionRules = getPerSessionPricingRules(pricingRules);
  if (normalizedSessionIds.length === 0) {
    return {
      entries: [],
      totalAmount: 0,
      unresolvedSessionIds: [],
      perSessionRules,
    };
  }

  const entries = [];
  const unresolvedSessionIds = [];
  normalizedSessionIds.forEach((sessionId) => {
    const matchedRule = pickBestPerSessionRule(sessionId, perSessionRules);
    if (!matchedRule) {
      unresolvedSessionIds.push(sessionId);
      return;
    }
    entries.push({
      sessionId,
      amount: matchedRule.amount,
      ruleId: matchedRule.ruleId,
      ruleLabel: matchedRule.label,
    });
  });

  return {
    entries,
    totalAmount: sumAmounts(entries.map((entry) => entry.amount)),
    unresolvedSessionIds,
    perSessionRules,
  };
};

const buildSessionLabelMap = (sessions = []) => {
  const map = new Map();
  getSortedSessions(sessions).forEach((session, index) => {
    const sessionId = resolveSessionId(session, `session-${index}`);
    map.set(sessionId, String(session?.label || `Session ${index + 1}`).trim());
  });
  return map;
};

const buildUnpriceableSessionsMessage = (sessionIds = [], sessions = []) => {
  if (!Array.isArray(sessionIds) || sessionIds.length === 0) {
    return 'Zusatz- oder Rest-Sessions können nicht berechnet werden, da keine passende "Pro Session"-Preisregel hinterlegt ist.';
  }
  const labelsBySessionId = buildSessionLabelMap(sessions);
  const labelList = sessionIds.map(
    (sessionId) => labelsBySessionId.get(sessionId) || sessionId,
  );
  return `Folgende Zusatz- oder Rest-Sessions können nicht berechnet werden: ${labelList.join(
    ", ",
  )}. Bitte hinterlegen Sie passende "Pro Session"-Preisregeln.`;
};

const calculateRuleBasePricing = ({ event, rule, selectedSessionIds = [] }) => {
  const pricingRules = event?.pricingRules || [];
  const sessions = event?.sessions || [];
  const safeSelectedSessionIds = normalizeSessionIds(selectedSessionIds);
  const ruleAmount = roundCurrency(normalizePositiveNumber(rule?.amount, 0));
  const defaults = {
    calculationError: "",
    effectiveSessionIds: safeSelectedSessionIds,
    selectedSessionCount: safeSelectedSessionIds.length,
    baseUnitPrice: 0,
    sessionsBooked: 0,
    sessionCountForRule: 0,
    packageMode: "",
    packageCount: 0,
    packageUnitAmount: 0,
    packageUnitTotal: 0,
    packageIncludedSessionIds: [],
    packageCoveredSessionCount: 0,
    remainderSessionIds: [],
    remainderSessionCount: 0,
    remainderUnitAmount: 0,
    additionalSessionIds: [],
    additionalSessionCount: 0,
    additionalSessionsUnitAmount: 0,
    perSessionUnitSubtotal: 0,
    packageSavingsUnitAmount: 0,
    nextPackageRemainingSessions: 0,
    nextPackagePotentialSavings: 0,
    nextPackageHintEligible: false,
  };

  if (rule.type === "fixed") {
    return {
      ...defaults,
      effectiveSessionIds: [],
      baseUnitPrice: ruleAmount,
      sessionsBooked: 0,
    };
  }

  if (rule.type === "per_session") {
    const sessionCount = safeSelectedSessionIds.length;
    if (sessionCount === 0) {
      return {
        ...defaults,
        calculationError: "Mindestens eine Session muss gewählt werden.",
      };
    }
    return {
      ...defaults,
      baseUnitPrice: roundCurrency(ruleAmount * sessionCount),
      sessionsBooked: sessionCount,
      sessionCountForRule: sessionCount,
    };
  }

  if (rule.type !== "package") {
    return {
      ...defaults,
      calculationError: "Unbekannter Preisregel-Typ.",
    };
  }

  const minSessions = normalizePackageMinSessions(rule);
  if (minSessions > 0) {
    if (safeSelectedSessionIds.length === 0) {
      return {
        ...defaults,
        packageMode: "min_sessions",
        packageUnitAmount: ruleAmount,
        calculationError: "Mindestens eine Session muss gewählt werden.",
      };
    }

    const packageCount = Math.floor(safeSelectedSessionIds.length / minSessions);
    const remainderSessionCount = safeSelectedSessionIds.length % minSessions;
    const packageUnitTotal = roundCurrency(packageCount * ruleAmount);
    if (remainderSessionCount === 0) {
      return {
        ...defaults,
        packageMode: "min_sessions",
        packageCount,
        packageUnitAmount: ruleAmount,
        packageUnitTotal,
        packageIncludedSessionIds: [...safeSelectedSessionIds],
        packageCoveredSessionCount: safeSelectedSessionIds.length,
        perSessionUnitSubtotal: 0,
        packageSavingsUnitAmount: 0,
        baseUnitPrice: packageUnitTotal,
        sessionsBooked: safeSelectedSessionIds.length,
      };
    }

    const perSessionResolution = resolvePerSessionPricingForSessions({
      sessionIds: safeSelectedSessionIds,
      pricingRules,
    });
    if (perSessionResolution.entries.length < remainderSessionCount) {
      const missingSessionIds =
        perSessionResolution.unresolvedSessionIds.length > 0
          ? perSessionResolution.unresolvedSessionIds
          : safeSelectedSessionIds;
      return {
        ...defaults,
        packageMode: "min_sessions",
        packageUnitAmount: ruleAmount,
        calculationError: buildUnpriceableSessionsMessage(
          missingSessionIds,
          sessions,
        ),
      };
    }

    const sortedByAmountAscending = [...perSessionResolution.entries].sort(
      (left, right) => {
        if (left.amount !== right.amount) return left.amount - right.amount;
        return String(left.sessionId).localeCompare(String(right.sessionId));
      },
    );
    const remainderEntries = sortedByAmountAscending.slice(0, remainderSessionCount);
    const remainderSessionIds = remainderEntries.map((entry) => entry.sessionId);
    const remainderSessionIdSet = new Set(remainderSessionIds);
    const packageSessionIds = safeSelectedSessionIds.filter(
      (sessionId) => !remainderSessionIdSet.has(sessionId),
    );
    const remainderUnitAmount = sumAmounts(
      remainderEntries.map((entry) => entry.amount),
    );

    const missingForNextPackage =
      remainderSessionCount > 0 ? minSessions - remainderSessionCount : 0;
    let nextPackageHintEligible = false;
    let nextPackagePotentialSavings = 0;

    if (missingForNextPackage > 0) {
      const selectedSet = new Set(safeSelectedSessionIds);
      const additionalCandidateAmounts = getAllSessionIds(sessions)
        .filter((sessionId) => !selectedSet.has(sessionId))
        .map((sessionId) =>
          pickBestPerSessionRule(sessionId, perSessionResolution.perSessionRules),
        )
        .filter(Boolean)
        .map((ruleEntry) => ruleEntry.amount)
        .sort((left, right) => left - right);

      if (additionalCandidateAmounts.length >= missingForNextPackage) {
        nextPackageHintEligible = true;
        const additionalCost = sumAmounts(
          additionalCandidateAmounts.slice(0, missingForNextPackage),
        );
        nextPackagePotentialSavings = roundCurrency(
          remainderUnitAmount + additionalCost - ruleAmount,
        );
      }
    }

    return {
      ...defaults,
      packageMode: "min_sessions",
      packageCount,
      packageUnitAmount: ruleAmount,
      packageUnitTotal,
      packageIncludedSessionIds: packageSessionIds,
      packageCoveredSessionCount: packageSessionIds.length,
      remainderSessionIds,
      remainderSessionCount,
      remainderUnitAmount,
      perSessionUnitSubtotal:
        perSessionResolution.unresolvedSessionIds.length === 0
          ? perSessionResolution.totalAmount
          : 0,
      packageSavingsUnitAmount:
        perSessionResolution.unresolvedSessionIds.length === 0
          ? roundCurrency(
              perSessionResolution.totalAmount - (packageUnitTotal + remainderUnitAmount),
            )
          : 0,
      baseUnitPrice: roundCurrency(packageUnitTotal + remainderUnitAmount),
      sessionsBooked: safeSelectedSessionIds.length,
      nextPackageRemainingSessions: missingForNextPackage,
      nextPackagePotentialSavings,
      nextPackageHintEligible,
    };
  }

  const linkedPackageSessionIds = getPackageIncludedSessionIds(rule, sessions);
  const effectiveSessionIds = normalizeSessionIds([
    ...linkedPackageSessionIds,
    ...safeSelectedSessionIds,
  ]);
  const linkedSessionSet = new Set(linkedPackageSessionIds);
  const additionalSessionIds = effectiveSessionIds.filter(
    (sessionId) => !linkedSessionSet.has(sessionId),
  );
  const additionalSessionResolution = resolvePerSessionPricingForSessions({
    sessionIds: additionalSessionIds,
    pricingRules,
  });
  if (additionalSessionResolution.unresolvedSessionIds.length > 0) {
    return {
      ...defaults,
      packageMode: "linked_sessions",
      effectiveSessionIds,
      selectedSessionCount: safeSelectedSessionIds.length,
      packageCount: 1,
      packageUnitAmount: ruleAmount,
      calculationError: buildUnpriceableSessionsMessage(
        additionalSessionResolution.unresolvedSessionIds,
        sessions,
      ),
    };
  }

  const linkedSessionResolution = resolvePerSessionPricingForSessions({
    sessionIds: linkedPackageSessionIds,
    pricingRules,
  });
  const linkedSessionSubtotal =
    linkedSessionResolution.unresolvedSessionIds.length === 0
      ? linkedSessionResolution.totalAmount
      : 0;
  const packageSavingsUnitAmount =
    linkedSessionResolution.unresolvedSessionIds.length === 0 &&
    linkedPackageSessionIds.length > 0
      ? roundCurrency(linkedSessionSubtotal - ruleAmount)
      : 0;

  return {
    ...defaults,
    packageMode: "linked_sessions",
    effectiveSessionIds,
    selectedSessionCount: safeSelectedSessionIds.length,
    packageCount: 1,
    packageUnitAmount: ruleAmount,
    packageUnitTotal: ruleAmount,
    packageIncludedSessionIds: linkedPackageSessionIds,
    packageCoveredSessionCount: linkedPackageSessionIds.length,
    additionalSessionIds,
    additionalSessionCount: additionalSessionIds.length,
    additionalSessionsUnitAmount: additionalSessionResolution.totalAmount,
    perSessionUnitSubtotal: additionalSessionResolution.totalAmount,
    packageSavingsUnitAmount,
    baseUnitPrice: roundCurrency(ruleAmount + additionalSessionResolution.totalAmount),
    sessionsBooked: effectiveSessionIds.length,
  };
};

const coerceAdminDiscount = (discount) => {
  if (!discount || typeof discount !== "object") return null;

  const type = String(discount.type || "")
    .trim()
    .toLowerCase();
  const value = normalizePositiveNumber(discount.value, 0);

  if (!["percentage", "fixed"].includes(type) || value <= 0) {
    return null;
  }

  return {
    label: String(discount.label || "").trim(),
    type,
    value: type === "percentage" ? clampPercentage(value) : value,
  };
};

export const validateBookingPackageRulesRestPricing = ({
  pricingRules = [],
  sessions = [],
}) => {
  const allSessionIds = getAllSessionIds(sessions);
  const perSessionRules = getPerSessionPricingRules(pricingRules);
  const hasPerSessionCoverage = (sessionId) =>
    Boolean(pickBestPerSessionRule(sessionId, perSessionRules));
  const labelBySessionId = buildSessionLabelMap(sessions);

  const packageRules = (Array.isArray(pricingRules) ? pricingRules : []).filter(
    (rule) => rule?.type === "package",
  );
  for (const rule of packageRules) {
    const packageLabel = String(rule?.label || "Paketregel").trim();
    const minSessions = normalizePackageMinSessions(rule);

    if (minSessions > 0) {
      if (allSessionIds.length === 0) {
        return {
          isValid: false,
          ruleId: String(rule?.ruleId || ""),
          message: `${packageLabel}: Für Paketregeln mit Mindestanzahl muss mindestens eine Session angelegt sein.`,
        };
      }
      const missingSessionIds = allSessionIds.filter(
        (sessionId) => !hasPerSessionCoverage(sessionId),
      );
      if (missingSessionIds.length > 0) {
        const missingLabels = missingSessionIds.map(
          (sessionId) => labelBySessionId.get(sessionId) || sessionId,
        );
        return {
          isValid: false,
          ruleId: String(rule?.ruleId || ""),
          message: `${packageLabel}: Rest-Sessions sind nicht vollständig bepreist. Fehlende "Pro Session"-Regeln für: ${missingLabels.join(
            ", ",
          )}.`,
        };
      }
      continue;
    }

    const packageSessionIds = getPackageIncludedSessionIds(rule, sessions);
    const packageSessionSet = new Set(packageSessionIds);
    const nonPackageSessionIds = allSessionIds.filter(
      (sessionId) => !packageSessionSet.has(sessionId),
    );
    if (nonPackageSessionIds.length === 0) continue;

    const missingSessionIds = nonPackageSessionIds.filter(
      (sessionId) => !hasPerSessionCoverage(sessionId),
    );
    if (missingSessionIds.length > 0) {
      const missingLabels = missingSessionIds.map(
        (sessionId) => labelBySessionId.get(sessionId) || sessionId,
      );
      return {
        isValid: false,
        ruleId: String(rule?.ruleId || ""),
        message: `${packageLabel}: Nicht verknüpfte Zusatz-Sessions benötigen "Pro Session"-Regeln. Fehlend für: ${missingLabels.join(
          ", ",
        )}.`,
      };
    }
  }

  return { isValid: true, ruleId: "", message: "" };
};

export const calculateBookingPricePreview = ({
  event,
  selectedPricingRuleId,
  selectedSessionIds = [],
  participantCount,
  memberParticipantCount = 0,
  bookingDate = new Date(),
  adminDiscount,
  pricingSnapshot,
}) => {
  const pricingRules = event?.pricingRules || [];
  const rule = getPricingRuleById(pricingRules, selectedPricingRuleId);

  const safeParticipantCount = Math.max(0, Number(participantCount) || 0);
  if (!rule || safeParticipantCount === 0) return null;

  const safeMemberParticipantCount = Math.min(
    safeParticipantCount,
    Math.max(0, Number(memberParticipantCount) || 0)
  );
  const safeSelectedSessionIds = normalizeSessionIds(selectedSessionIds);

  const basePricing = calculateRuleBasePricing({
    event,
    rule,
    selectedSessionIds: safeSelectedSessionIds,
  });
  const baseUnitPrice = basePricing.baseUnitPrice;
  const effectiveSessionIds = basePricing.effectiveSessionIds;
  const sessionCountForRule = basePricing.sessionCountForRule;

  const memberDiscountPercent =
    safeMemberParticipantCount > 0 ? clampPercentage(rule.memberDiscount) : 0;
  const siblingDiscountPercent = event?.registrationConfig?.discountRules?.siblingDiscount?.enabled
    ? clampPercentage(event?.registrationConfig?.discountRules?.siblingDiscount?.percentage)
    : 0;
  const earlyBirdRule = event?.registrationConfig?.discountRules?.earlyBirdDiscount || {};
  const earlyBirdDiscountType =
    earlyBirdRule?.type === "fixed" ? "fixed" : "percentage";
  const earlyBirdDiscountPercent =
    earlyBirdRule?.enabled && earlyBirdDiscountType === "percentage"
      ? clampPercentage(earlyBirdRule?.percentage)
      : 0;
  const earlyBirdDiscountFixedAmount =
    earlyBirdRule?.enabled && earlyBirdDiscountType === "fixed"
      ? roundCurrency(normalizePositiveNumber(earlyBirdRule?.amount, 0))
      : 0;
  const earlyBirdDeadline = toValidDate(
    earlyBirdRule?.deadline
  );
  const normalizedBookingDate = toValidDate(bookingDate) || new Date();
  const earlyBirdDiscountEligible =
    (earlyBirdDiscountPercent > 0 || earlyBirdDiscountFixedAmount > 0) &&
    Boolean(earlyBirdDeadline) &&
    normalizedBookingDate <= earlyBirdDeadline;

  const defaultGrossAmount = roundCurrency(baseUnitPrice * safeParticipantCount);
  const defaultMemberDiscountAmount = roundCurrency(
    baseUnitPrice * safeMemberParticipantCount * (memberDiscountPercent / 100)
  );
  const defaultAfterMemberAmount = roundCurrency(
    defaultGrossAmount - defaultMemberDiscountAmount
  );

  const siblingParticipantFactor =
    safeParticipantCount > 1
      ? (safeParticipantCount - 1) / safeParticipantCount
      : 0;
  const defaultSiblingDiscountAmount =
    siblingDiscountPercent > 0 && siblingParticipantFactor > 0
      ? roundCurrency(
          defaultAfterMemberAmount *
            siblingParticipantFactor *
            (siblingDiscountPercent / 100)
        )
      : 0;
  const defaultAfterSiblingAmount = roundCurrency(
    defaultAfterMemberAmount - defaultSiblingDiscountAmount
  );

  const defaultEarlyBirdDiscountAmount = earlyBirdDiscountEligible
    ? roundCurrency(
        earlyBirdDiscountType === "fixed"
          ? Math.min(defaultAfterSiblingAmount, earlyBirdDiscountFixedAmount)
          : defaultAfterSiblingAmount * (earlyBirdDiscountPercent / 100)
      )
    : 0;
  const defaultAfterEarlyBirdAmount = roundCurrency(
    defaultAfterSiblingAmount - defaultEarlyBirdDiscountAmount
  );

  const normalizedAdminDiscount = coerceAdminDiscount(adminDiscount);
  const defaultAdminDiscountAmount = normalizedAdminDiscount
    ? roundCurrency(
        normalizedAdminDiscount.type === "percentage"
          ? defaultAfterEarlyBirdAmount * (normalizedAdminDiscount.value / 100)
          : Math.min(defaultAfterEarlyBirdAmount, normalizedAdminDiscount.value)
      )
    : 0;
  const defaultTotalAmount = Math.max(
    0,
    roundCurrency(defaultAfterEarlyBirdAmount - defaultAdminDiscountAmount)
  );

  let grossAmount = defaultGrossAmount;
  let siblingDiscountAmount = defaultSiblingDiscountAmount;
  let earlyBirdDiscountAmount = defaultEarlyBirdDiscountAmount;
  let adminDiscountAmount = defaultAdminDiscountAmount;
  let totalAmount = defaultTotalAmount;
  let memberDiscountAmount = defaultMemberDiscountAmount;

  if (pricingSnapshot && typeof pricingSnapshot === "object") {
    if (isFiniteNumber(pricingSnapshot.grossAmount)) {
      grossAmount = roundCurrency(pricingSnapshot.grossAmount);
    }
    if (isFiniteNumber(pricingSnapshot.siblingDiscountApplied)) {
      siblingDiscountAmount = roundCurrency(pricingSnapshot.siblingDiscountApplied);
    }
    if (isFiniteNumber(pricingSnapshot.earlyBirdDiscountApplied)) {
      earlyBirdDiscountAmount = roundCurrency(pricingSnapshot.earlyBirdDiscountApplied);
    }
    if (isFiniteNumber(pricingSnapshot.adminDiscountApplied)) {
      adminDiscountAmount = roundCurrency(pricingSnapshot.adminDiscountApplied);
    }
    if (isFiniteNumber(pricingSnapshot.totalAmount)) {
      totalAmount = roundCurrency(pricingSnapshot.totalAmount);
    } else if (isFiniteNumber(pricingSnapshot.finalAmount)) {
      totalAmount = roundCurrency(
        normalizePositiveNumber(pricingSnapshot.finalAmount, 0) * safeParticipantCount
      );
    }

    memberDiscountAmount = roundCurrency(
      Math.max(
        0,
        grossAmount -
          siblingDiscountAmount -
          earlyBirdDiscountAmount -
          adminDiscountAmount -
          totalAmount
      )
    );
  }

  const afterMemberAmount = roundCurrency(grossAmount - memberDiscountAmount);
  const afterSiblingAmount = roundCurrency(afterMemberAmount - siblingDiscountAmount);
  const afterEarlyBirdAmount = roundCurrency(afterSiblingAmount - earlyBirdDiscountAmount);
  const siblingDiscountEligible =
    safeParticipantCount > 1 &&
    (siblingDiscountPercent > 0 || siblingDiscountAmount > 0);
  const resolvedEarlyBirdEligible = earlyBirdDiscountEligible || earlyBirdDiscountAmount > 0;

  return {
    rule,
    calculationError: basePricing.calculationError || "",
    participantCount: safeParticipantCount,
    memberCount: safeMemberParticipantCount,
    nonMemberCount: Math.max(0, safeParticipantCount - safeMemberParticipantCount),
    selectedSessionCount: basePricing.selectedSessionCount,
    effectiveSessionIds,
    baseUnitPrice,
    sessionsBooked: basePricing.sessionsBooked,
    sessionCountForRule,
    packageMode: basePricing.packageMode,
    packageCount: basePricing.packageCount,
    packageUnitAmount: basePricing.packageUnitAmount,
    packageUnitTotal: basePricing.packageUnitTotal,
    packageIncludedSessionIds: basePricing.packageIncludedSessionIds,
    packageCoveredSessionCount: basePricing.packageCoveredSessionCount,
    remainderSessionIds: basePricing.remainderSessionIds,
    remainderSessionCount: basePricing.remainderSessionCount,
    remainderUnitAmount: basePricing.remainderUnitAmount,
    additionalSessionIds: basePricing.additionalSessionIds,
    additionalSessionCount: basePricing.additionalSessionCount,
    additionalSessionsUnitAmount: basePricing.additionalSessionsUnitAmount,
    perSessionUnitSubtotal: basePricing.perSessionUnitSubtotal,
    packageSavingsUnitAmount: basePricing.packageSavingsUnitAmount,
    nextPackageRemainingSessions: basePricing.nextPackageRemainingSessions,
    nextPackagePotentialSavings: basePricing.nextPackagePotentialSavings,
    nextPackageHintEligible: basePricing.nextPackageHintEligible,
    grossAmount,
    memberDiscountPercent,
    memberDiscountAmount,
    afterMemberAmount,
    siblingDiscountPercent,
    siblingDiscountAmount,
    siblingDiscountEligible,
    siblingDiscountedParticipants: Math.max(0, safeParticipantCount - 1),
    afterSiblingAmount,
    earlyBirdDiscountPercent,
    earlyBirdDiscountType,
    earlyBirdDiscountFixedAmount,
    earlyBirdDiscountAmount,
    earlyBirdDiscountEligible: resolvedEarlyBirdEligible,
    earlyBirdDeadline,
    afterEarlyBirdAmount,
    adminDiscountAmount,
    adminDiscountLabel: normalizedAdminDiscount?.label || "",
    adminDiscountType: normalizedAdminDiscount?.type || "",
    adminDiscountValue: normalizedAdminDiscount?.value || 0,
    totalAmount,
  };
};
